import React, { createContext, useContext, useReducer, useEffect, ReactNode, useRef } from 'react';
import { AuthState, User, LoginRequest, RegisterRequest } from '../types/auth';
import { AuthAPI } from '../services/authApi';
import toast from 'react-hot-toast';
import logger from '../utils/logger';
import { isPrerendering } from '../utils/prerender';

declare global {
  interface Window {
    google?: any;
  }
}

const GOOGLE_IDENTITY_SCRIPT_SRC = 'https://accounts.google.com/gsi/client';
let googleIdentityServicesPromise: Promise<any> | null = null;

const MICROSOFT_AUTH_MESSAGE_TYPE = 'microsoft-auth-result';
const PKCE_ALLOWED_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789-._~';

const base64UrlEncode = (buffer: ArrayBuffer): string => {
  const bytes = new Uint8Array(buffer);
  let binary = '';

  for (let i = 0; i < bytes.byteLength; i += 1) {
    binary += String.fromCharCode(bytes[i]);
  }

  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
};

const generateRandomString = (length: number): string => {
  if (typeof window === 'undefined' || !window.crypto?.getRandomValues) {
    throw new Error('Secure random generator is not available.');
  }

  const randomValues = new Uint8Array(length);
  window.crypto.getRandomValues(randomValues);

  return Array.from(randomValues)
    .map((value) => PKCE_ALLOWED_CHARS.charAt(value % PKCE_ALLOWED_CHARS.length))
    .join('');
};

const generateCodeVerifier = (): string => generateRandomString(96);

const generateState = (): string => generateRandomString(32);

const generateCodeChallenge = async (codeVerifier: string): Promise<string> => {
  if (typeof window === 'undefined' || !window.crypto?.subtle) {
    throw new Error('Cryptographic utilities are not available.');
  }

  const encoder = new TextEncoder();
  const data = encoder.encode(codeVerifier);
  const digest = await window.crypto.subtle.digest('SHA-256', data);
  return base64UrlEncode(digest);
};

const loadGoogleIdentityServices = (): Promise<any> => {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Identity Services are unavailable in this environment.'));
  }

  if (window.google?.accounts?.oauth2?.initCodeClient) {
    return Promise.resolve(window.google);
  }

  if (googleIdentityServicesPromise) {
    return googleIdentityServicesPromise;
  }

  googleIdentityServicesPromise = new Promise((resolve, reject) => {
    const existingScript = document.querySelector<HTMLScriptElement>(`script[src="${GOOGLE_IDENTITY_SCRIPT_SRC}"]`);

    const handleLoaded = () => {
      if (window.google?.accounts?.oauth2?.initCodeClient) {
        resolve(window.google);
      } else {
        reject(new Error('Google Identity Services failed to initialize.'));
      }
    };

    const handleError = () => {
      reject(new Error('Failed to load Google Identity Services.'));
    };

    if (existingScript) {
      existingScript.addEventListener('load', handleLoaded, { once: true });
      existingScript.addEventListener('error', handleError, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src = GOOGLE_IDENTITY_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => {
      script.setAttribute('data-loaded', 'true');
      handleLoaded();
    };
    script.onerror = handleError;
    document.head.appendChild(script);
  }).catch(error => {
    googleIdentityServicesPromise = null;
    throw error;
  });

  return googleIdentityServicesPromise;
};

// Initial state
const initialState: AuthState = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  anonymousSessionId: null,
  accessToken: null,
};

// Action types
type AuthAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_USER'; payload: User | null }
  | { type: 'SET_AUTHENTICATED'; payload: boolean }
  | { type: 'SET_ANONYMOUS_SESSION'; payload: string | null }
  | { type: 'SET_ACCESS_TOKEN'; payload: string | null }
  | { type: 'LOGOUT' };

// Reducer
function authReducer(state: AuthState, action: AuthAction): AuthState {
  switch (action.type) {
    case 'SET_LOADING':
      return { ...state, isLoading: action.payload };
    case 'SET_USER':
      return { ...state, user: action.payload };
    case 'SET_AUTHENTICATED':
      return { ...state, isAuthenticated: action.payload };
    case 'SET_ANONYMOUS_SESSION':
      return { ...state, anonymousSessionId: action.payload };
    case 'SET_ACCESS_TOKEN':
      return { ...state, accessToken: action.payload };
    case 'LOGOUT':
      return {
        ...state,
        user: null,
        isAuthenticated: false,
        accessToken: null,
      };
    default:
      return state;
  }
}

// Context interface
interface SocialLoginOptions {
  rememberMe?: boolean;
  context?: 'login' | 'register';
}

interface AuthContextType extends AuthState {
  login: (credentials: LoginRequest) => Promise<boolean>;
  loginWithGoogle: (options?: SocialLoginOptions) => Promise<boolean>;
  loginWithMicrosoft: (options?: SocialLoginOptions) => Promise<boolean>;
  register: (userData: RegisterRequest) => Promise<boolean>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
  getOrCreateAnonymousSession: (preferences?: any) => Promise<void>;
  onAuthStateChange?: (callback: (isAuthenticated: boolean, user: User | null) => void) => void;
}

// Create context
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Provider component
interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const authCallbacksRef = useRef<Array<(isAuthenticated: boolean, user: User | null) => void>>([]);

  // Initialize authentication state
  useEffect(() => {
    initializeAuth();
  }, []);

  // Notify callbacks when auth state changes
  useEffect(() => {
    authCallbacksRef.current.forEach(callback => {
      callback(state.isAuthenticated, state.user);
    });
  }, [state.isAuthenticated, state.user]); // Removed authCallbacks dependency

  const onAuthStateChange = (callback: (isAuthenticated: boolean, user: User | null) => void) => {
    authCallbacksRef.current.push(callback);
    
    // Return cleanup function
    return () => {
      const index = authCallbacksRef.current.indexOf(callback);
      if (index > -1) {
        authCallbacksRef.current.splice(index, 1);
      }
    };
  };

  const initializeAuth = async () => {
    try {
      dispatch({ type: 'SET_LOADING', payload: true });

      // Skip network/auth init during react-snap prerender
      if (isPrerendering()) {
        logger.auth('Prerender detected; skipping auth initialization');
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      // Check for existing anonymous session and access token
      const anonymousSessionId = localStorage.getItem('anonymousSessionId');
      const accessToken = localStorage.getItem('accessToken');
      
      logger.auth('initializeAuth called', {
        anonymousSessionId,
        accessToken,
        stateAccessToken: state.accessToken
      });

      // If we have an anonymous session, use it and skip auth check
      if (anonymousSessionId) {
        dispatch({ type: 'SET_ANONYMOUS_SESSION', payload: anonymousSessionId });
        logger.auth('Using existing anonymous session, skipping auth check');
        dispatch({ type: 'SET_LOADING', payload: false });
        return;
      }

      // If we have an access token, try to get current user
      if (accessToken) {
        // Check if we should consider the token expired based on remember me preference
        const rememberMe = localStorage.getItem('rememberMe') === 'true';
        const tokenAge = Date.now() - (parseInt(localStorage.getItem('accessTokenCreatedAt') || '0', 10));
        const maxTokenAge = rememberMe ? 30 * 24 * 60 * 60 * 1000 : 15 * 60 * 1000; // 30 days vs 15 minutes
        
        if (tokenAge > maxTokenAge) {
          logger.auth('Access token expired based on remember me preference; clearing stored token');
          localStorage.removeItem('accessToken');
          localStorage.removeItem('accessTokenCreatedAt');
          localStorage.removeItem('rememberMe');
          dispatch({ type: 'SET_ACCESS_TOKEN', payload: null });
          await getOrCreateAnonymousSession();
          dispatch({ type: 'SET_LOADING', payload: false });
          return;
        }
        
        logger.auth('Found access token, attempting to get current user...');
        const response = await AuthAPI.getCurrentUser();
        logger.auth('getCurrentUser response:', {
          success: response.success,
          hasUser: !!(response.data?.user || response.user),
          error: response.message
        });
        
        // Check both new data structure and legacy structure
        const user = response.data?.user || response.user;
        const accessToken = response.data?.accessToken || response.accessToken;
        
        if (response.success && user) {
          logger.auth('User authenticated successfully');
          dispatch({ type: 'SET_USER', payload: user });
          dispatch({ type: 'SET_AUTHENTICATED', payload: true });
          if (accessToken) {
            dispatch({ type: 'SET_ACCESS_TOKEN', payload: accessToken });
          }

        } else {
          // Access token is invalid, try to refresh using the cookie-based refresh token
          logger.auth('Access token invalid; attempting refresh');
          try {
            const refreshResponse = await AuthAPI.refreshToken();
                         if (refreshResponse.success && refreshResponse.accessToken) {
               dispatch({ type: 'SET_ACCESS_TOKEN', payload: refreshResponse.accessToken });
               localStorage.setItem('accessToken', refreshResponse.accessToken);
               localStorage.setItem('accessTokenCreatedAt', Date.now().toString());
               
               // Now try to get the user again with the new token
               logger.auth('Token refreshed; fetching user data');
               const userResponse = await AuthAPI.getCurrentUser();
               const user = userResponse.data?.user || userResponse.user;
               
               if (userResponse.success && user) {
                 dispatch({ type: 'SET_USER', payload: user });
                 dispatch({ type: 'SET_AUTHENTICATED', payload: true });
                 logger.auth('Fetched user data after token refresh');
                 return; // Successfully authenticated
               } else {
                 logger.error('Failed to fetch user data after token refresh', userResponse);
               }
             }
                     } catch (refreshError) {
             logger.error('Token refresh failed', refreshError);
           }
          
          // Refresh failed, clear access token and create anonymous session
          logger.auth('Token refresh flow failed; falling back to anonymous session');
          localStorage.removeItem('accessToken');
          dispatch({ type: 'SET_ACCESS_TOKEN', payload: null });
          await getOrCreateAnonymousSession();
        }
      } else {
        // No session and no token, create anonymous session
        await getOrCreateAnonymousSession();
      }
    } catch (error) {
      logger.error('Failed to initialize auth', error);
      // Clear invalid access token if auth check fails
      if (localStorage.getItem('accessToken')) {
        localStorage.removeItem('accessToken');
        localStorage.removeItem('accessTokenCreatedAt');
        localStorage.removeItem('rememberMe');
        dispatch({ type: 'SET_ACCESS_TOKEN', payload: null });
      }
      // Ensure we have an anonymous session even if auth check fails
      if (!state.anonymousSessionId) {
        await getOrCreateAnonymousSession();
      }
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const login = async (credentials: LoginRequest): Promise<boolean> => {
    try {
      logger.auth('Starting credential login');
      dispatch({ type: 'SET_LOADING', payload: true });
      
      const response = await AuthAPI.login(credentials);
      
      // Check both new data structure and legacy structure
      const user = response.data?.user || response.user;
      const accessToken = response.data?.accessToken || response.accessToken;
      
      if (response.success && user && accessToken) {
        logger.auth('Login successful; updating state');
        dispatch({ type: 'SET_USER', payload: user });
        dispatch({ type: 'SET_AUTHENTICATED', payload: true });
        dispatch({ type: 'SET_ACCESS_TOKEN', payload: accessToken });
        
        // Store access token in localStorage for API calls
        localStorage.setItem('accessToken', accessToken);
        localStorage.setItem('accessTokenCreatedAt', Date.now().toString());
        
        // Store remember me preference for future reference
        if (credentials.rememberMe) {
          localStorage.setItem('rememberMe', 'true');
        } else {
          localStorage.removeItem('rememberMe');
        }
        
        // Clear anonymous session
        if (state.anonymousSessionId) {
          localStorage.removeItem('anonymousSessionId');
          dispatch({ type: 'SET_ANONYMOUS_SESSION', payload: null });
        }
        
        logger.auth('Login completed successfully');
        toast.success('Login successful!');
        return true;
      } else {
        logger.error('Login failed', response.message);
        toast.error(response.message || 'Login failed');
        return false;
      }
    } catch (error) {
      logger.error('Login error', error);
      toast.error('Login failed. Please try again.');
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const loginWithMicrosoft = async (options: SocialLoginOptions = {}): Promise<boolean> => {
    if (isPrerendering()) {
      logger.auth('Skipping Microsoft login during prerender');
      return false;
    }

    if (typeof window === 'undefined') {
      logger.auth('Microsoft login attempted without browser environment');
      return false;
    }

    if (!window.crypto?.getRandomValues || !window.crypto?.subtle) {
      toast.error('Microsoft sign-in is not supported in this browser.');
      return false;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const clientId = process.env.REACT_APP_MICROSOFT_CLIENT_ID;

      if (!clientId) {
        logger.auth('Microsoft client ID missing; aborting login');
        toast.error('Microsoft sign-in is not configured.');
        dispatch({ type: 'SET_LOADING', payload: false });
        return false;
      }

      const tenantId = process.env.REACT_APP_MICROSOFT_TENANT_ID || 'common';
      const defaultRedirectUri = `${window.location.origin}/auth/microsoft/callback`;
      const configuredRedirectUri = process.env.REACT_APP_MICROSOFT_REDIRECT_URI;
      const redirectUri = configuredRedirectUri && configuredRedirectUri.trim().length > 0
        ? configuredRedirectUri.trim()
        : defaultRedirectUri;

      const rememberMePreference = Boolean(options.rememberMe);
      const codeVerifier = generateCodeVerifier();
      const codeChallenge = await generateCodeChallenge(codeVerifier);
      const stateValue = generateState();
      const storageKey = `microsoft-code-verifier:${stateValue}`;

      try {
        sessionStorage.setItem(storageKey, JSON.stringify({
          codeVerifier,
          redirectUri,
          createdAt: Date.now(),
        }));
      } catch (storageError) {
        logger.error('Failed to persist Microsoft PKCE data', storageError);
        toast.error('Microsoft sign-in is unavailable right now.');
        dispatch({ type: 'SET_LOADING', payload: false });
        return false;
      }

      const authorizeUrl = new URL(`https://login.microsoftonline.com/${tenantId}/oauth2/v2.0/authorize`);
      authorizeUrl.searchParams.set('client_id', clientId);
      authorizeUrl.searchParams.set('response_type', 'code');
      authorizeUrl.searchParams.set('redirect_uri', redirectUri);
      authorizeUrl.searchParams.set('response_mode', 'query');
      authorizeUrl.searchParams.set('scope', 'openid profile email offline_access https://graph.microsoft.com/User.Read');
      authorizeUrl.searchParams.set('state', stateValue);
      authorizeUrl.searchParams.set('code_challenge', codeChallenge);
      authorizeUrl.searchParams.set('code_challenge_method', 'S256');

      return await new Promise<boolean>((resolve) => {
        let completed = false;
        let popup: Window | null = null;
        let popupMonitor: number | null = null;
        let hasReceivedMessage = false;

        const handleMessage = async (event: MessageEvent) => {
          if (event.origin !== window.location.origin) {
            return;
          }

          const data = event.data || {};
          if (data?.type !== MICROSOFT_AUTH_MESSAGE_TYPE || data.state !== stateValue) {
            return;
          }

          hasReceivedMessage = true;

          if (popupMonitor !== null) {
            window.clearInterval(popupMonitor);
            popupMonitor = null;
          }

          window.removeEventListener('message', handleMessage);

          if (popup && !popup.closed) {
            popup.close();
          }

          if (data.error) {
            const description = typeof data.errorDescription === 'string' && data.errorDescription.trim().length > 0
              ? data.errorDescription
              : 'Microsoft sign-in failed.';
            finish(false, description);
            return;
          }

          if (!data.code) {
            finish(false, 'Microsoft sign-in did not return a valid authorization code.');
            return;
          }

          try {
            const storedDataRaw = sessionStorage.getItem(storageKey);
            let storedData: { codeVerifier?: string; redirectUri?: string } | null = null;

            if (storedDataRaw) {
              try {
                storedData = JSON.parse(storedDataRaw);
              } catch (parseError) {
                logger.error('Failed to parse stored Microsoft PKCE data', parseError);
              }
            }

            const verifierFromStorage = typeof storedData?.codeVerifier === 'string'
              ? storedData?.codeVerifier
              : null;
            const redirectUriForExchange = typeof storedData?.redirectUri === 'string' && storedData.redirectUri.length > 0
              ? storedData.redirectUri
              : redirectUri;

            if (!verifierFromStorage) {
              finish(false, 'Microsoft sign-in data expired. Please try again.');
              return;
            }

            const response = await AuthAPI.exchangeMicrosoftCode({
              code: data.code,
              codeVerifier: verifierFromStorage,
              redirectUri: redirectUriForExchange,
              anonymousSessionId: state.anonymousSessionId || undefined,
              rememberMe: rememberMePreference,
            });

            const user = response.data?.user || response.user;
            const accessToken = response.data?.accessToken || response.accessToken;

            if (response.success && user && accessToken) {
              dispatch({ type: 'SET_USER', payload: user });
              dispatch({ type: 'SET_AUTHENTICATED', payload: true });
              dispatch({ type: 'SET_ACCESS_TOKEN', payload: accessToken });

              localStorage.setItem('accessToken', accessToken);
              localStorage.setItem('accessTokenCreatedAt', Date.now().toString());

              if (rememberMePreference) {
                localStorage.setItem('rememberMe', 'true');
              } else {
                localStorage.removeItem('rememberMe');
              }

              if (state.anonymousSessionId) {
                localStorage.removeItem('anonymousSessionId');
                dispatch({ type: 'SET_ANONYMOUS_SESSION', payload: null });
              }

              finish(true);
            } else {
              finish(false, response.message || 'Microsoft sign-in failed.');
            }
          } catch (error: any) {
            logger.error('Microsoft sign-in exchange error', error);
            const apiMessage = error?.response?.data?.message || error?.message;
            finish(false, apiMessage || 'Microsoft sign-in failed.');
          }
        };

        const clearResources = () => {
          window.removeEventListener('message', handleMessage);
          if (popupMonitor !== null) {
            window.clearInterval(popupMonitor);
            popupMonitor = null;
          }
          sessionStorage.removeItem(storageKey);

          try {
            if (popup && !popup.closed) {
              popup.close();
            }
          } catch (popupError) {
            logger.debug('Popup close threw', popupError);
          }
        };

        const finish = (success: boolean, message?: string) => {
          if (completed) {
            return;
          }
          completed = true;
          clearResources();
          dispatch({ type: 'SET_LOADING', payload: false });

          if (success) {
            logger.auth('Microsoft sign-in completed successfully');
            toast.success(options.context === 'register' ? 'Account created with Microsoft!' : 'Signed in with Microsoft!');
          } else if (message) {
            logger.error('Microsoft sign-in failed', message);
            toast.error(message);
          }

          resolve(success);
        };

        window.addEventListener('message', handleMessage);

        popup = window.open(
          authorizeUrl.toString(),
          'microsoft-auth',
          'width=600,height=700,menubar=no,toolbar=no,status=no,resizable=yes,scrollbars=yes'
        );

        if (!popup) {
          finish(false, 'Microsoft sign-in popup was blocked. Please enable popups and try again.');
          return;
        }

        popup.focus();

        popupMonitor = window.setInterval(() => {
          if ((!popup || popup.closed) && !hasReceivedMessage) {
            finish(false, 'Microsoft sign-in window was closed before completion.');
          }
        }, 500);
      });
    } catch (error) {
      logger.error('Microsoft sign-in initialization error', error);
      dispatch({ type: 'SET_LOADING', payload: false });
      toast.error('Microsoft sign-in failed. Please try again.');
      return false;
    }
  };

  const loginWithGoogle = async (options: SocialLoginOptions = {}): Promise<boolean> => {
    if (isPrerendering()) {
      logger.auth('Skipping Google login during prerender');
      return false;
    }

    if (typeof window === 'undefined') {
      logger.auth('Google login attempted without browser environment');
      return false;
    }

    dispatch({ type: 'SET_LOADING', payload: true });

    try {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;

      if (!clientId) {
        logger.auth('Google client ID missing; aborting login');
        toast.error('Google sign-in is not configured.');
        dispatch({ type: 'SET_LOADING', payload: false });
        return false;
      }

      const google = await loadGoogleIdentityServices();

      if (!google?.accounts?.oauth2?.initCodeClient) {
        toast.error('Google sign-in is not available right now.');
        dispatch({ type: 'SET_LOADING', payload: false });
        return false;
      }

      const rememberMePreference = Boolean(options.rememberMe);

      return await new Promise<boolean>((resolve) => {
        let completed = false;

        const finish = (success: boolean, message?: string) => {
          if (completed) {
            return;
          }
          completed = true;
          dispatch({ type: 'SET_LOADING', payload: false });

          if (success) {
            toast.success(options.context === 'register' ? 'Account created with Google!' : 'Signed in with Google!');
          } else if (message) {
            toast.error(message);
          }

          resolve(success);
        };

        const codeClient = google.accounts.oauth2.initCodeClient({
          client_id: clientId,
          scope: 'openid email profile',
          ux_mode: 'popup',
          redirect_uri: 'postmessage',
          prompt: options.context === 'register' ? 'consent' : undefined,
          callback: async (codeResponse: any) => {
            if (!codeResponse?.code) {
              finish(false, 'Google sign-in was cancelled.');
              return;
            }

            try {
              const response = await AuthAPI.exchangeGoogleCode({
                code: codeResponse.code,
                anonymousSessionId: state.anonymousSessionId || undefined,
                rememberMe: rememberMePreference,
              });

              const user = response.data?.user || response.user;
              const accessToken = response.data?.accessToken || response.accessToken;

              if (response.success && user && accessToken) {
                dispatch({ type: 'SET_USER', payload: user });
                dispatch({ type: 'SET_AUTHENTICATED', payload: true });
                dispatch({ type: 'SET_ACCESS_TOKEN', payload: accessToken });

                localStorage.setItem('accessToken', accessToken);
                localStorage.setItem('accessTokenCreatedAt', Date.now().toString());

                if (rememberMePreference) {
                  localStorage.setItem('rememberMe', 'true');
                } else {
                  localStorage.removeItem('rememberMe');
                }

                if (state.anonymousSessionId) {
                  localStorage.removeItem('anonymousSessionId');
                  dispatch({ type: 'SET_ANONYMOUS_SESSION', payload: null });
                }

                finish(true);
              } else {
                finish(false, response.message || 'Google sign-in failed.');
              }
            } catch (error: any) {
              logger.error('Google sign-in exchange error', error);
              const apiMessage = error?.response?.data?.message || error?.message;
              finish(false, apiMessage || 'Google sign-in failed.');
            }
          },
          error_callback: (error: any) => {
            logger.auth('Google sign-in error callback', error);
            const message = typeof error === 'string'
              ? error
              : error?.type === 'popup_closed'
                ? 'Google sign-in window was closed before completion.'
                : error?.message || 'Google sign-in failed.';
            finish(false, message);
          },
        });

        try {
          codeClient.requestCode();
        } catch (error: any) {
          logger.error('Failed to trigger Google code request', error);
          finish(false, 'Google sign-in failed to start.');
        }
      });
    } catch (error) {
      logger.error('Google sign-in initialization error', error);
      dispatch({ type: 'SET_LOADING', payload: false });
      toast.error('Google sign-in failed. Please try again.');
      return false;
    }
  };

  const register = async (userData: RegisterRequest): Promise<boolean> => {
    try {
      logger.auth('Starting registration');
      dispatch({ type: 'SET_LOADING', payload: true });
      
      // Include anonymous session ID if available
      const requestData = {
        ...userData,
        anonymousSessionId: state.anonymousSessionId || undefined,
      };
      
      const response = await AuthAPI.register(requestData);
      
      // Check both new data structure and legacy structure
      const user = response.data?.user || response.user;
      const accessToken = response.data?.accessToken || response.accessToken;
      
      if (response.success && user && accessToken) {
        logger.auth('Registration successful; updating state');
        dispatch({ type: 'SET_USER', payload: user });
        dispatch({ type: 'SET_AUTHENTICATED', payload: true });
        dispatch({ type: 'SET_ACCESS_TOKEN', payload: accessToken });
        
        // Store access token in localStorage for API calls
        localStorage.setItem('accessToken', accessToken);
        
        // Clear anonymous session
        if (state.anonymousSessionId) {
          localStorage.removeItem('anonymousSessionId');
          dispatch({ type: 'SET_ANONYMOUS_SESSION', payload: null });
        }
        
        logger.auth('Registration completed successfully');
        toast.success('Account created successfully!');
        return true;
      } else {
        logger.error('Registration failed', response.message);
        toast.error(response.message || 'Registration failed');
        return false;
      }
    } catch (error) {
      logger.error('Registration error', error);
      toast.error('Registration failed. Please try again.');
      return false;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false });
    }
  };

  const logout = async () => {
    try {
      await AuthAPI.logout();
    } catch (error) {
      logger.error('Logout error', error);
    } finally {
      dispatch({ type: 'LOGOUT' });
      
      // Clear access token and related data from localStorage
      localStorage.removeItem('accessToken');
      localStorage.removeItem('accessTokenCreatedAt');
      localStorage.removeItem('rememberMe');
      
      // Create new anonymous session after logout
      await getOrCreateAnonymousSession();
      
      toast.success('Logged out successfully');
    }
  };

  const refreshUser = async () => {
    try {
      const response = await AuthAPI.getCurrentUser();
      const user = response.data?.user || response.user;
      
      if (response.success && user) {
        dispatch({ type: 'SET_USER', payload: user });
        dispatch({ type: 'SET_AUTHENTICATED', payload: true });
      } else {
        dispatch({ type: 'LOGOUT' });
        localStorage.removeItem('accessToken');
      }
    } catch (error) {
      logger.error('Failed to refresh user', error);
      dispatch({ type: 'LOGOUT' });
      localStorage.removeItem('accessToken');
    }
  };

  const getOrCreateAnonymousSession = async (preferences?: any) => {
    try {
      logger.auth('Creating or retrieving anonymous session');
      logger.auth('Current anonymous session ID', state.anonymousSessionId);
      
      const response = await AuthAPI.getOrCreateAnonymousSession(
        state.anonymousSessionId || undefined,
        preferences
      );
      
      
      if (response.success && response.sessionId) {
        logger.auth('Anonymous session created or retrieved successfully');
        dispatch({ type: 'SET_ANONYMOUS_SESSION', payload: response.sessionId });
        localStorage.setItem('anonymousSessionId', response.sessionId);
        logger.auth('Stored anonymous session in state and localStorage', response.sessionId);
      } else {
        logger.error('Failed to create or retrieve anonymous session', response);
        // Don't create fallback IDs that won't work with the backend
        toast.error('Failed to create anonymous session. Please refresh the page.');
      }
    } catch (error) {
      logger.error('Error creating anonymous session', error);
      // Don't create fallback IDs that won't work with the backend
      toast.error('Failed to create anonymous session. Please refresh the page.');
    }
  };

  const value: AuthContextType = {
    ...state,
    login,
    loginWithGoogle,
    loginWithMicrosoft,
    register,
    logout,
    refreshUser,
    getOrCreateAnonymousSession,
    onAuthStateChange,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Hook to use auth context
export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

